import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const KitContents = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const kitItems = [
    {
      icon: "🐟",
      title: "Bassin poisson",
      description: "Bassin spécialement conçu pour l'élevage de truites",
      features: ["Capacité optimale", "Système d'oxygénation", "Contrôle température"],
    },
    {
      icon: "🌿",
      title: "Bac culture de plante",
      description: "Système de culture hydroponique intégré",
      features: ["Substrat inclus", "Irrigation automatique", "Éclairage LED"],
    },
    {
      icon: "⚙️",
      title: "Filtre et pompe",
      description: "Système de filtration et circulation d'eau",
      features: ["Filtre biologique", "Pompe silencieuse", "système de contrôle intelligent"],
    },
    {
      icon: "🌱",
      title: "Graines et alevins",
      description: "Kit de démarrage complet",
      features: ["Graines bio sélectionnées", "Alevins de truite", "Nourriture 6 mois"],
    },
    {
      icon: "📱",
      title: "Dashboard mobile",
      description: "Application de monitoring et contrôle",
      features: ["Suivi en temps réel", "Alertes automatiques", "Conseils personnalisés"],
    },
  ];

  return (
    <section className="py-20 bg-aquacream relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,_theme(colors.emerald.500)_25%,_transparent_25%,_transparent_75%,_theme(colors.emerald.500)_75%,_theme(colors.emerald.500)),_linear-gradient(45deg,_theme(colors.emerald.500)_25%,_transparent_25%,_transparent_75%,_theme(colors.emerald.500)_75%,_theme(colors.emerald.500))] bg-[size:20px_20px] bg-[position:0_0,_10px_10px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Que contient le kit testeur?
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Un système complet clé en main pour démarrer votre production
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {kitItems.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative bg-aquacream rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-emerald-200"
            >
              {/* Icon */}
              <div className="text-6xl mb-6 group-hover:scale-110 transition-transform duration-300">
                {item.icon}
              </div>

              {/* Content */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-aquagreen transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
                
                {/* Features */}
                <ul className="space-y-2">
                  {item.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center text-sm text-gray-500"
                    >
                      <div className="w-2 h-2 bg-aquagreen rounded-full mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-blue-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-aquagreen to-aquablue rounded-3xl p-12 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              Prêt à rejoindre nos testeurs bêta?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Seulement 10 places disponibles pour cette première phase
            </p>
            <motion.a
              href="https://forms.gle/gv7DaeyYDsvayxjXA"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-8 py-4 bg-white text-aquagreen font-bold text-lg rounded-full hover:bg-gray-100 transition-all duration-300 shadow-2xl"
            >
              Réserver ma place
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default KitContents;